import { useEffect, useRef, useState } from 'react';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { matchRoutes, Outlet, useLocation, useParams } from 'react-router';
import { ROLE } from '@/common/role';
import CesiumViewer from '@/components/CesiumViewer';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { usePermission } from '@/hooks/use-permission';
import { cn } from '@/lib/utils';
import { checkAssetIdIsValid } from '@/lib/utils/asset';
import { useMobileAssetTab } from '@/context/MobileAssetTabContext';
import { TilesetLocationEditProvider } from '@/context/TilesetLocationEditContext';
import { AssetReadOnlyProvider } from '@/context/AssetReadOnlyContext';
import { AssetSidebar } from './AssetSidebar';

const cesiumVisibleRoutes = [
	{ path: '/assets/:assetId' },
	{ path: '/assets/:assetId/inspections/:inspectionId' },
	{ path: '/assets/:assetId/inspections/:inspectionId/damages/:damageId' },
];

export type MapAction = {
	type: 'unlink' | 'zoom' | 'refresh';
	payload?: any;
};

export type AssetContextType = {
	createdPolygons: any[];
	mapActions: React.Dispatch<React.SetStateAction<MapAction | null>>;
	activeDamage: React.Dispatch<React.SetStateAction<any>>;
	calculateDamageSize: (
		damageId: string,
		measurementType: 'area' | 'perimeter'
	) => string | number | null;
};

export function AssetMainOutlet() {
	const leftPanelRef = useRef<ImperativePanelHandle>(null);

	const location = useLocation();
	const { assetId } = useParams<{ assetId: string }>();
	const [showCesiumStatus, setShowCesiumStatus] = useState(true);
	const hasInspectionManagementPagePermission = usePermission(ROLE.INSPECTION_MANAGEMENT_PAGE_VIEW);

	const [createdPolygons, setCreatedPolygons] = useState<any[]>([]);
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
	const [mapTabVisited, setMapTabVisited] = useState(false);

	const { activeTab, setActiveTab, setShowMapTab } = useMobileAssetTab();

	const [mapAction, setMapAction] = useState<MapAction | null>(null);
	const [activeDamage, setActiveDamage] = useState<any>({});

	const [calculateSizeFn, setCalculateSizeFn] = useState<
		((id: string, measurementType: 'area' | 'perimeter') => string | number | null) | undefined
	>(undefined);

	const handleCalculateSize = (id: string, measurementType: 'area' | 'perimeter') => {
		if (calculateSizeFn) {
			return calculateSizeFn(id, measurementType);
		}
		console.warn('3D Viewer is not active, cannot calculate size.');
		return null;
	};

	// The "new" placeholder in /assets/new is not an asset: there is nothing to show in 3D,
	// and the viewer's per-asset loaders would otherwise query the API with an id that
	// does not exist (404 "Asset not found" + "No 3D tiles found" toasts).
	// A deleted asset opened as a preview (GH #331) keeps the map for the same layout, but the viewer
	// shows only where it is: its models and damage shapes were deleted with it.
	const hasAsset = checkAssetIdIsValid(assetId);
	const showCesium = hasAsset && !!matchRoutes(cesiumVisibleRoutes, location);

	// Ensure that the Cesium viewer is shown if the user has permission
	useEffect(() => {
		if (!hasInspectionManagementPagePermission) {
			setShowCesiumStatus(false);
			return;
		}
		setShowCesiumStatus(true);
	}, [hasInspectionManagementPagePermission]);

	useEffect(() => {
		if (!leftPanelRef.current) return;

		// If the Cesium viewer is not supposed to be shown, ensure the left panel takes full width
		if (!showCesiumStatus) {
			leftPanelRef.current.resize(100);
			return;
		}

		// If the Cesium viewer is supposed to be shown, adjust the left panel size accordingly
		if (showCesium) {
			leftPanelRef.current.resize(40);
		} else {
			leftPanelRef.current.resize(100);
		}
	}, [showCesium, showCesiumStatus]);

	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth < 1024);
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, []);

	useEffect(() => {
		if (activeTab === 'map') setMapTabVisited(true);
	}, [activeTab]);

	useEffect(() => {
		setShowMapTab(showCesium);
		if (!showCesium && activeTab === 'map') {
			setActiveTab('info');
		}
	}, [showCesium, activeTab, setActiveTab, setShowMapTab]);

	const outletContext = {
		createdPolygons: createdPolygons,
		mapActions: setMapAction,
		activeDamage: setActiveDamage,
		calculateDamageSize: handleCalculateSize,
	};

	return (
		<AssetReadOnlyProvider>
		<TilesetLocationEditProvider>
		<div className="flex flex-1 overflow-hidden">
			<AssetSidebar />

			<main className="flex-1 overflow-x-hidden relative">
				{isMobile ? (
					<div className="h-[calc(100vh-64px)] flex flex-col">
						{/* Tab Content */}
						<div className={cn('flex-1 overflow-auto', activeTab !== 'info' && 'hidden')}>
							<Outlet context={outletContext} />
						</div>
						{hasAsset && mapTabVisited && (
							<div className={cn('flex-1 relative bg-muted', activeTab !== 'map' && 'hidden')}>
								<CesiumViewer
									onPolygonCreated={setCreatedPolygons}
									activeAction={mapAction}
									activeDamage={activeDamage}
									registerSizeCalculator={setCalculateSizeFn}
								/>
							</div>
						)}
					</div>
				) : (
					<div className="h-[calc(100vh-64px)] w-full">
						<ResizablePanelGroup direction="horizontal">
							<ResizablePanel
								ref={leftPanelRef}
								defaultSize={40}
								minSize={30}
								className="flex flex-col border-r"
							>
								<Outlet context={outletContext} />
							</ResizablePanel>

							{showCesium && <ResizableHandle className="w-1 hover:bg-primary transition-colors" />}

							<ResizablePanel
								collapsible
								collapsedSize={showCesium ? 50 : 0}
								defaultSize={0}
								minSize={30}
							>
								<div className="flex-1 relative bg-muted">
									{hasAsset && (
										<CesiumViewer
											onPolygonCreated={setCreatedPolygons}
											activeAction={mapAction}
											activeDamage={activeDamage}
											registerSizeCalculator={setCalculateSizeFn}
										/>
									)}
								</div>
							</ResizablePanel>
						</ResizablePanelGroup>
					</div>
				)}
			</main>
		</div>
		</TilesetLocationEditProvider>
		</AssetReadOnlyProvider>
	);
}
